import { useCallback, useEffect, useState } from "react";
import { History, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../../lib/utils";
import { Badge } from "../../../components/ui/badge";
import {
  CollabDraftAttributeTestColumn,
  CollabDraftSupportColumn,
  CollabDraftTestingTable,
  CollabProposedAttribute,
  CollabRemovedColumns,
  CollabTestingTableChanges,
} from "../../../models/audit/collabPlanTypes";
import { collabFieldKeys as fieldKeys } from "./planFieldKeys";
import { attributeTestColumnKey, withRemovedInPlace } from "./planGhosts";
import { EDIT_CARD_CLASS, RequestDelete } from "./planReveal";
import {
  CollapsibleRow,
  EditableDetailField,
  FieldRevealAnchor,
  GhostDetailField,
  RemovedBadge,
} from "./planFields";
import { ColumnHistoryDialog } from "./ColumnHistoryDialog";
import { CollabAnalyticsColumnsSection } from "./CollabAnalyticsColumnsSection";
import type { AnalyticsOwnerKind } from "../../../lib/analyticsOwner";
import {
  snapshotToAttrTestChanges,
  snapshotToSupportChanges,
} from "./columnHistoryRestore";

/** Which column's version history is open. Id-keyed (not index-keyed) so the
 * target stays correct if the plan shifts under the open dialog. */
interface HistoryTarget {
  tableId: string;
  kind: "attribute_test" | "support";
  columnId: string;
}

/**
 * "Test Instructions" view: a per-testing-table breakdown of the detailed
 * how-to-test content. Where the Structure tab lists column headers only, this
 * surfaces (and, when editable, lets the user edit) the per-procedure testing
 * context, each attribute test column's pass/fail/N-A criteria and procedure
 * guidance, and each support column's description.
 *
 * Generic in the table type rather than fixed to one, so an entity whose tables
 * only allow a subset of `procedureType` keeps that constraint at the type
 * level. Edits leave here as (table, changes) and the owner turns them into
 * whatever patch its own API takes.
 */
export const CollabTestInstructionsView = <
  TTable extends CollabDraftTestingTable,
>({
  plan,
  editable,
  onTableUpdate,
  requestDelete,
  removedItems,
  analyticsOwnerKind,
}: {
  plan: {
    proposedAttributes: CollabProposedAttribute[] | null;
    testingTables: TTable[];
  };
  editable: boolean;
  /**
   * Describes an edit rather than applying it: the owner merges `changes` onto
   * its own concrete table, which is what keeps a narrower `procedureType`
   * intact through an edit here.
   */
  onTableUpdate?: (table: TTable, changes: CollabTestingTableChanges) => void;
  requestDelete: RequestDelete;
  removedItems: CollabRemovedColumns;
  analyticsOwnerKind: AnalyticsOwnerKind;
}) => {
  const proposedAttributes = plan.proposedAttributes ?? [];

  const updateTable = useCallback(
    (table: TTable, changes: CollabTestingTableChanges) => {
      onTableUpdate?.(table, changes);
    },
    [onTableUpdate],
  );

  const updateAttrTestColumn = useCallback(
    (
      table: TTable,
      colIndex: number,
      changes: Partial<CollabDraftAttributeTestColumn>,
    ) => {
      const attributeTestColumns = table.attributeTestColumns.map((col, i) =>
        i === colIndex ? { ...col, ...changes } : col,
      );
      onTableUpdate?.(table, { attributeTestColumns });
    },
    [onTableUpdate],
  );

  const updateSupportColumn = useCallback(
    (
      table: TTable,
      colIndex: number,
      changes: Partial<CollabDraftSupportColumn>,
    ) => {
      const supportColumns = table.supportColumns.map((col, i) =>
        i === colIndex ? { ...col, ...changes } : col,
      );
      onTableUpdate?.(table, { supportColumns });
    },
    [onTableUpdate],
  );

  const removeAttrTestColumn = useCallback(
    (table: TTable, colIndex: number) => {
      const attributeTestColumns = table.attributeTestColumns.filter(
        (_, i) => i !== colIndex,
      );
      onTableUpdate?.(table, { attributeTestColumns });
    },
    [onTableUpdate],
  );

  const removeSupportColumn = useCallback(
    (table: TTable, colIndex: number) => {
      const supportColumns = table.supportColumns.filter(
        (_, i) => i !== colIndex,
      );
      onTableUpdate?.(table, { supportColumns });
    },
    [onTableUpdate],
  );

  const [historyTarget, setHistoryTarget] = useState<HistoryTarget | null>(
    null,
  );

  // If the open dialog's table/column vanishes from the plan, drop the target
  // too — a lingering target would spontaneously reopen the dialog if a column
  // with the same id later reappeared in the plan.
  useEffect(() => {
    if (!historyTarget) return;
    const table = plan.testingTables.find(
      (t) => t.id === historyTarget.tableId,
    );
    const columns =
      historyTarget.kind === "attribute_test"
        ? table?.attributeTestColumns
        : table?.supportColumns;
    if (!columns?.some((c) => c.id === historyTarget.columnId)) {
      setHistoryTarget(null);
    }
  }, [historyTarget, plan.testingTables]);

  // Re-derive the open dialog's table/column from the live plan each render:
  // a restore (or agent turn) replaces the plan, and the dialog's "current"
  // side of the diff should track it. Closes implicitly if the column is gone.
  const historyDialog = (() => {
    if (!historyTarget) return null;
    const table = plan.testingTables.find(
      (t) => t.id === historyTarget.tableId,
    );
    if (!table?.id) return null;
    const tableId = table.id;
    const close = () => setHistoryTarget(null);

    if (historyTarget.kind === "attribute_test") {
      const colIndex = table.attributeTestColumns.findIndex(
        (c) => c.id === historyTarget.columnId,
      );
      const column = table.attributeTestColumns[colIndex];
      if (!column) return null;
      return (
        <ColumnHistoryDialog
          procedureId={tableId}
          target={{ kind: "attribute_test", column }}
          proposedAttributes={proposedAttributes}
          onRestore={(snapshot) => {
            const result = snapshotToAttrTestChanges(
              snapshot,
              proposedAttributes,
            );
            if (result) {
              updateAttrTestColumn(table, colIndex, result.changes);
              if (result.unrestoredAttributeId !== null) {
                toast.info(
                  "Restored without the attribute assignment. That version's attribute no longer exists in this plan.",
                );
              }
            } else {
              toast.error("This version can't be restored onto this column.");
            }
            close();
          }}
          onClose={close}
        />
      );
    }

    const colIndex = table.supportColumns.findIndex(
      (c) => c.id === historyTarget.columnId,
    );
    const column = table.supportColumns[colIndex];
    if (!column) return null;
    return (
      <ColumnHistoryDialog
        procedureId={tableId}
        target={{ kind: "support", column }}
        proposedAttributes={proposedAttributes}
        onRestore={(snapshot) => {
          const changes = snapshotToSupportChanges(snapshot);
          if (changes) {
            updateSupportColumn(table, colIndex, changes);
          } else {
            toast.error("This version can't be restored onto this column.");
          }
          close();
        }}
        onClose={close}
      />
    );
  })();

  if (plan.testingTables.length === 0) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <p className="text-sm text-stone-400">No testing tables defined.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {plan.testingTables.map((table, tableIndex) => {
        const hasColumns =
          table.supportColumns.length > 0 ||
          table.attributeTestColumns.length > 0 ||
          table.analyticsColumns.length > 0;
        const removedTestColumns =
          removedItems.testColumns.get(table.name) ?? [];
        const removedSupportColumns =
          removedItems.supportColumns.get(table.name) ?? [];
        const removedAnalyticsGhosts =
          removedItems.analyticsColumns.get(table.name) ?? [];

        return (
          <div key={tableIndex} className={cn(EDIT_CARD_CLASS, "p-3")}>
            <CollapsibleRow
              defaultOpen
              revealPrefixes={[
                `table:${table.name}:`,
                `col:${table.name}:`,
                `support:${table.name}:`,
                `analytics:${table.name}:`,
              ]}
              header={
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-stone-800">
                    {table.name}
                  </span>
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1.5 py-0.5 font-normal capitalize"
                  >
                    {table.procedureType}
                  </Badge>
                </span>
              }
            >
              <div className="space-y-3">
                {(editable ||
                  table.testingContext ||
                  table.documentationStandards) && (
                  <CollapsibleRow
                    defaultOpen={false}
                    revealPrefixes={[`table:${table.name}:`]}
                    header={
                      <span className="text-xs font-medium text-stone-600">
                        Testing Context
                      </span>
                    }
                  >
                    <div className="space-y-1.5 rounded-md border border-stone-200 bg-white px-2.5 py-2">
                      <EditableDetailField
                        label="Context"
                        value={table.testingContext}
                        editable={editable}
                        placeholder="How testing should be performed..."
                        onCommit={(v) =>
                          updateTable(table, { testingContext: v || null })
                        }
                        fieldKey={fieldKeys.tableTestingContext(table.name)}
                      />
                      <EditableDetailField
                        label="Documentation Standards"
                        value={table.documentationStandards}
                        editable={editable}
                        placeholder="Documentation standards to follow..."
                        onCommit={(v) =>
                          updateTable(table, {
                            documentationStandards: v || null,
                          })
                        }
                        fieldKey={fieldKeys.tableDocStandards(table.name)}
                      />
                    </div>
                  </CollapsibleRow>
                )}

                {(table.attributeTestColumns.length > 0 ||
                  removedTestColumns.length > 0) && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-stone-600">
                      Attribute Test Columns (
                      {table.attributeTestColumns.length})
                    </div>
                    <div className="space-y-1.5">
                      {withRemovedInPlace(
                        table.attributeTestColumns,
                        removedTestColumns,
                      ).map((entry) => {
                        if (entry.kind === "removed") {
                          const col = entry.item;
                          const colKey = attributeTestColumnKey(col);
                          return (
                            <FieldRevealAnchor
                              key={`removed-${colKey}`}
                              fieldKey={fieldKeys.colName(table.name, colKey)}
                            >
                              <CollapsibleRow
                                className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2"
                                header={
                                  <span className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-medium text-red-400 line-through">
                                      {col.name ??
                                        col.attributeReference ??
                                        "Test"}
                                    </span>
                                    {col.attributeReference && (
                                      <span className="rounded-sm bg-red-100 px-1.5 py-0.5 text-[11px] text-red-600 line-through">
                                        {col.attributeReference}
                                      </span>
                                    )}
                                    <RemovedBadge />
                                  </span>
                                }
                              >
                                <div className="space-y-1.5">
                                  <GhostDetailField
                                    label="Pass"
                                    value={col.passCriteria}
                                  />
                                  <GhostDetailField
                                    label="Fail"
                                    value={col.failCriteria}
                                  />
                                  <GhostDetailField
                                    label="N/A"
                                    value={col.naCriteria}
                                  />
                                  <GhostDetailField
                                    label="Procedure Guidance"
                                    value={col.procedureGuidance}
                                  />
                                  <GhostDetailField
                                    label="Tolerances"
                                    value={col.tolerances}
                                  />
                                  <GhostDetailField
                                    label="Expected Evidence"
                                    value={col.expectedEvidences}
                                  />
                                </div>
                              </CollapsibleRow>
                            </FieldRevealAnchor>
                          );
                        }
                        const col = entry.item;
                        const i = entry.index;
                        return (
                          <FieldRevealAnchor
                            key={i}
                            fieldKey={fieldKeys.colName(
                              table.name,
                              attributeTestColumnKey(col),
                            )}
                          >
                            <CollapsibleRow
                              revealPrefixes={[
                                `col:${table.name}:${attributeTestColumnKey(col)}:`,
                              ]}
                              className="group/col rounded-md border border-stone-200 bg-white px-2.5 py-2"
                              header={
                                <span className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-medium text-stone-700">
                                    {col.name ??
                                      col.attributeReference ??
                                      "Test"}
                                  </span>
                                  {col.attributeReference && (
                                    <span className="rounded-sm bg-stone-200 px-1.5 py-0.5 text-[11px] text-stone-600">
                                      {col.attributeReference}
                                    </span>
                                  )}
                                </span>
                              }
                              action={
                                editable ? (
                                  <span className="flex shrink-0 items-center gap-0.5">
                                    {table.id && col.id && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setHistoryTarget({
                                            tableId: table.id ?? "",
                                            kind: "attribute_test",
                                            columnId: col.id ?? "",
                                          })
                                        }
                                        className="rounded-sm p-1 text-stone-400 opacity-0 transition-opacity hover:bg-stone-200 hover:text-stone-600 group-hover/col:opacity-100"
                                        aria-label="Column version history"
                                      >
                                        <History className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        requestDelete({
                                          title:
                                            "Remove attribute test column?",
                                          description: `"${col.name ?? col.attributeReference ?? "This column"}" will be removed from ${table.name}.`,
                                          onConfirm: () =>
                                            removeAttrTestColumn(table, i),
                                        })
                                      }
                                      className="rounded-sm p-1 text-stone-400 opacity-0 transition-opacity hover:bg-stone-200 hover:text-stone-600 group-hover/col:opacity-100"
                                      aria-label="Remove column"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </span>
                                ) : undefined
                              }
                            >
                              <div className="space-y-1.5">
                                <EditableDetailField
                                  label="Name"
                                  value={col.name}
                                  editable={editable}
                                  placeholder="Column name..."
                                  onCommit={(v) =>
                                    updateAttrTestColumn(table, i, {
                                      name: v || null,
                                    })
                                  }
                                  fieldKey={fieldKeys.colName(
                                    table.name,
                                    attributeTestColumnKey(col),
                                  )}
                                  // The row header (a FieldRevealAnchor for this
                                  // same key) already flashes; don't double it.
                                  suppressReveal
                                  singleLine
                                />
                                <EditableDetailField
                                  label="Pass"
                                  value={col.passCriteria}
                                  editable={editable}
                                  placeholder="Pass criteria..."
                                  onCommit={(v) =>
                                    updateAttrTestColumn(table, i, {
                                      passCriteria: v,
                                    })
                                  }
                                  fieldKey={fieldKeys.colPass(
                                    table.name,
                                    attributeTestColumnKey(col),
                                  )}
                                />
                                <EditableDetailField
                                  label="Fail"
                                  value={col.failCriteria}
                                  editable={editable}
                                  placeholder="Fail criteria..."
                                  onCommit={(v) =>
                                    updateAttrTestColumn(table, i, {
                                      failCriteria: v || null,
                                    })
                                  }
                                  fieldKey={fieldKeys.colFail(
                                    table.name,
                                    attributeTestColumnKey(col),
                                  )}
                                />
                                <EditableDetailField
                                  label="N/A"
                                  value={col.naCriteria}
                                  editable={editable}
                                  placeholder="N/A criteria..."
                                  onCommit={(v) =>
                                    updateAttrTestColumn(table, i, {
                                      naCriteria: v || null,
                                    })
                                  }
                                  fieldKey={fieldKeys.colNa(
                                    table.name,
                                    attributeTestColumnKey(col),
                                  )}
                                />
                                <EditableDetailField
                                  label="Procedure Guidance"
                                  value={col.procedureGuidance}
                                  editable={editable}
                                  placeholder="Procedure guidance..."
                                  onCommit={(v) =>
                                    updateAttrTestColumn(table, i, {
                                      procedureGuidance: v || null,
                                    })
                                  }
                                  fieldKey={fieldKeys.colGuidance(
                                    table.name,
                                    attributeTestColumnKey(col),
                                  )}
                                />
                                <EditableDetailField
                                  label="Tolerances"
                                  value={col.tolerances}
                                  editable={editable}
                                  placeholder="Tolerances..."
                                  onCommit={(v) =>
                                    updateAttrTestColumn(table, i, {
                                      tolerances: v || null,
                                    })
                                  }
                                  fieldKey={fieldKeys.colTolerances(
                                    table.name,
                                    attributeTestColumnKey(col),
                                  )}
                                />
                                <EditableDetailField
                                  label="Expected Evidence"
                                  value={col.expectedEvidences}
                                  editable={editable}
                                  placeholder="Expected evidence..."
                                  onCommit={(v) =>
                                    updateAttrTestColumn(table, i, {
                                      expectedEvidences: v || null,
                                    })
                                  }
                                  fieldKey={fieldKeys.colExpectedEvidences(
                                    table.name,
                                    attributeTestColumnKey(col),
                                  )}
                                />
                              </div>
                            </CollapsibleRow>
                          </FieldRevealAnchor>
                        );
                      })}
                    </div>
                  </div>
                )}

                <CollabAnalyticsColumnsSection
                  table={table}
                  removedGhosts={removedAnalyticsGhosts}
                  editable={editable}
                  proposedAttributes={proposedAttributes}
                  onTableUpdate={onTableUpdate}
                  requestDelete={requestDelete}
                  analyticsOwnerKind={analyticsOwnerKind}
                />

                {(table.supportColumns.length > 0 ||
                  removedSupportColumns.length > 0) && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-stone-600">
                      Support Columns ({table.supportColumns.length})
                    </div>
                    <div className="space-y-1.5">
                      {withRemovedInPlace(
                        table.supportColumns,
                        removedSupportColumns,
                      ).map((entry) => {
                        if (entry.kind === "removed") {
                          const col = entry.item;
                          return (
                            <FieldRevealAnchor
                              key={`removed-${col.name}`}
                              fieldKey={fieldKeys.supportName(
                                table.name,
                                col.name,
                              )}
                            >
                              <CollapsibleRow
                                className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2"
                                header={
                                  <span className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-medium text-red-400 line-through">
                                      {col.name}
                                    </span>
                                    <RemovedBadge />
                                  </span>
                                }
                              >
                                <GhostDetailField
                                  label="Description"
                                  value={col.description}
                                />
                              </CollapsibleRow>
                            </FieldRevealAnchor>
                          );
                        }
                        const col = entry.item;
                        const i = entry.index;
                        return (
                          <FieldRevealAnchor
                            key={i}
                            fieldKey={fieldKeys.supportName(
                              table.name,
                              col.name,
                            )}
                          >
                            <CollapsibleRow
                              revealPrefixes={[
                                `support:${table.name}:${col.name}:`,
                              ]}
                              className="group/col rounded-md border border-stone-200 bg-white px-2.5 py-2"
                              header={
                                <span className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-medium text-stone-700">
                                    {col.name}
                                  </span>
                                  <Badge
                                    variant="outline"
                                    className="rounded-sm px-1.5 py-0.5 text-[11px] font-normal"
                                  >
                                    {col.dataType === "string"
                                      ? "Text"
                                      : "Number"}
                                  </Badge>
                                </span>
                              }
                              action={
                                editable ? (
                                  <span className="flex shrink-0 items-center gap-0.5">
                                    {table.id && col.id && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setHistoryTarget({
                                            tableId: table.id ?? "",
                                            kind: "support",
                                            columnId: col.id ?? "",
                                          })
                                        }
                                        className="rounded-sm p-1 text-stone-400 opacity-0 transition-opacity hover:bg-stone-200 hover:text-stone-600 group-hover/col:opacity-100"
                                        aria-label="Column version history"
                                      >
                                        <History className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        requestDelete({
                                          title: "Remove support column?",
                                          description: `"${col.name}" will be removed from ${table.name}.`,
                                          onConfirm: () =>
                                            removeSupportColumn(table, i),
                                        })
                                      }
                                      className="rounded-sm p-1 text-stone-400 opacity-0 transition-opacity hover:bg-stone-200 hover:text-stone-600 group-hover/col:opacity-100"
                                      aria-label="Remove column"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </span>
                                ) : undefined
                              }
                            >
                              <EditableDetailField
                                label="Name"
                                value={col.name}
                                editable={editable}
                                placeholder="Column name..."
                                onCommit={(v) =>
                                  updateSupportColumn(table, i, {
                                    name: v || col.name,
                                  })
                                }
                                fieldKey={fieldKeys.supportName(
                                  table.name,
                                  col.name,
                                )}
                                suppressReveal
                                singleLine
                              />
                              <EditableDetailField
                                label="Description"
                                value={col.description}
                                editable={editable}
                                placeholder="What data this column captures..."
                                onCommit={(v) =>
                                  updateSupportColumn(table, i, {
                                    description: v,
                                  })
                                }
                                fieldKey={fieldKeys.supportDescription(
                                  table.name,
                                  col.name,
                                )}
                              />
                            </CollapsibleRow>
                          </FieldRevealAnchor>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!hasColumns &&
                  removedTestColumns.length === 0 &&
                  removedSupportColumns.length === 0 &&
                  removedAnalyticsGhosts.length === 0 &&
                  table.removedAnalyticsColumns.length === 0 && (
                    <p className="text-xs text-stone-400">
                      No columns defined for this table.
                    </p>
                  )}
              </div>
            </CollapsibleRow>
          </div>
        );
      })}
      {historyDialog}
    </div>
  );
};
